import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Search, Users, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompaniesPage = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    gst_number: "",
    pan_number: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    email: "",
    logo_url: "",
    status: "active"
  });

  const [assignForm, setAssignForm] = useState({
    user_id: ""
  });

  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [companiesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/companies`),
        axios.get(`${API_URL}/users`)
      ]);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCompany) {
        await axios.put(`${API_URL}/companies/${selectedCompany.id}`, formData);
        toast.success("Company updated successfully");
      } else {
        await axios.post(`${API_URL}/companies`, formData);
        toast.success("Company created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      code: company.code,
      gst_number: company.gst_number || "",
      pan_number: company.pan_number || "",
      address: company.address || "",
      city: company.city || "",
      state: company.state || "",
      pincode: company.pincode || "",
      phone: company.phone || "",
      email: company.email || "",
      logo_url: company.logo_url || "",
      status: company.status || "active"
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/companies/${selectedCompany.id}`);
      toast.success("Company deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/companies/${selectedCompany.id}/assign-user?user_id=${assignForm.user_id}`);
      toast.success("User assigned to company");
      setAssignDialogOpen(false);
      setAssignForm({ user_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign user");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    
    try {
      const response = await axios.post(`${API_URL}/upload-image`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData({ ...formData, logo_url: response.data.url });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const resetForm = () => {
    setSelectedCompany(null);
    setFormData({
      name: "",
      code: "",
      gst_number: "",
      pan_number: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      phone: "",
      email: "",
      logo_url: "",
      status: "active"
    });
  };

  const openAssignDialog = (company) => {
    setSelectedCompany(company);
    setAssignDialogOpen(true);
  };

  const filteredCompanies = companies.filter(company => 
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.gst_number && company.gst_number.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCompanyUsers = (companyId) => {
    return users.filter(u => u.company_id === companyId);
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="bg-card border-border max-w-md">
          <CardContent className="p-8 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">Only Super Admins can manage companies.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Company Management</h1>
          <p className="text-muted-foreground">Manage multiple companies with separate data</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary" data-testid="add-company-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, code, or GST..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 bg-secondary/50"
          data-testid="company-search"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-primary">{companies.length}</div>
            <p className="text-sm text-muted-foreground">Total Companies</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">{companies.filter(c => c.status === "active").length}</div>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-400">{users.filter(u => u.company_id).length}</div>
            <p className="text-sm text-muted-foreground">Assigned Users</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-400">{users.filter(u => !u.company_id).length}</div>
            <p className="text-sm text-muted-foreground">Unassigned Users</p>
          </CardContent>
        </Card>
      </div>

      {/* Companies Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Company</TableHead>
                <TableHead className="text-muted-foreground">Code</TableHead>
                <TableHead className="text-muted-foreground">GST Number</TableHead>
                <TableHead className="text-muted-foreground">Location</TableHead>
                <TableHead className="text-muted-foreground">Users</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow key={company.id} className="border-border" data-testid={`company-row-${company.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt={company.name} className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{company.name}</p>
                          {company.email && <p className="text-xs text-muted-foreground">{company.email}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{company.code}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{company.gst_number}</TableCell>
                    <TableCell>
                      <span className="text-sm">{company.city}{company.state ? `, ${company.state}` : ""}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-500/20 text-blue-400">
                        {getCompanyUsers(company.id).length} users
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={company.status === "active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {company.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openAssignDialog(company)} className="h-8 w-8" data-testid={`assign-users-${company.id}`}>
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(company)} className="h-8 w-8" data-testid={`edit-company-${company.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedCompany(company); setDeleteDialogOpen(true); }} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-company-${company.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCompany ? "Edit Company" : "Add New Company"}</DialogTitle>
            <DialogDescription>
              {selectedCompany ? "Update company information" : "Create a new company with separate data"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="Ardhanageshwar Logistics"
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Company Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="bg-secondary/50"
                  placeholder="ARDLOG"
                  required
                  disabled={selectedCompany}
                  data-testid="company-code-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gst_number">GST Number *</Label>
                <Input
                  id="gst_number"
                  value={formData.gst_number}
                  onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
                  className="bg-secondary/50"
                  placeholder="18ABECA5341A2Z4"
                  required
                  data-testid="company-gst-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pan_number">PAN Number</Label>
                <Input
                  id="pan_number"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                  className="bg-secondary/50"
                  placeholder="ABECA5341A"
                  data-testid="company-pan-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-secondary/50"
                placeholder="123 Logistics Park"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="Mumbai"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="Maharashtra"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pincode">Pincode</Label>
                <Input
                  id="pincode"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="400001"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="+91 93951 70701"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="info@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Logo</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo_upload"
                  />
                  <label
                    htmlFor="logo_upload"
                    className={`flex items-center gap-2 px-4 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingLogo ? "opacity-50" : ""}`}
                  >
                    <Upload className="w-4 h-4" />
                    {formData.logo_url ? "Change Logo" : "Upload Logo"}
                  </label>
                  {formData.logo_url && (
                    <img src={formData.logo_url} alt="Logo" className="w-10 h-10 rounded object-cover" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-secondary/50" data-testid="company-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary" data-testid="save-company-btn">
                {selectedCompany ? "Update Company" : "Create Company"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Company</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedCompany?.name}? This will affect all users assigned to this company.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-company">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Assign User to {selectedCompany?.name}</DialogTitle>
            <DialogDescription>Select a user to assign to this company</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={assignForm.user_id} onValueChange={(v) => setAssignForm({ user_id: v })}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Choose a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => !u.company_id || u.company_id !== selectedCompany?.id).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email}) - {u.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Current users in company */}
            {selectedCompany && (
              <div className="space-y-2">
                <Label>Currently Assigned Users ({getCompanyUsers(selectedCompany.id).length})</Label>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {getCompanyUsers(selectedCompany.id).map((u) => (
                    <div key={u.id} className="text-sm p-2 bg-secondary/30 rounded">
                      {u.name} - {u.role}
                    </div>
                  ))}
                  {getCompanyUsers(selectedCompany.id).length === 0 && (
                    <p className="text-sm text-muted-foreground">No users assigned yet</p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary" disabled={!assignForm.user_id}>Assign User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesPage;
